import type { ReactNode } from 'react';
import { createContext, useCallback, useState } from 'react';
import { Snackbar } from '#/components/Snackbar';

export type NotificationLevel = 'critical' | 'error' | 'success';

export type DurationForEachNotificationLevel = { [key in NotificationLevel]: number };

export const durationForEachNotificationLevel: DurationForEachNotificationLevel = {
  critical: Number.POSITIVE_INFINITY,
  error: 10000,
  success: 5000,
};

export type SnackbarMessage = {
  id: number;
  /**
   * 通知メッセージ
   */
  message: string;
  /**
   * 閉じるボタンを非表示にするかどうか
   */
  hideCloseButon?: boolean;
  /**
   * 通知の重要度
   *
   * - 'critical': 特に重要度が高く、ユーザーに必ず読んで欲しい内容。閉じるボタンを押すまで表示
   * - 'error': エラー結果。10秒間表示
   * - 'success': 成功した操作結果。5秒間表示
   */
  notificationLevel: NotificationLevel;
};

export type SnackbarStore = {
  add: (snackbar: SnackbarMessage) => void;
  remove: (id: number) => void;
  messages: SnackbarMessage[];
};
const initialSnackbarContext = {
  add: () => undefined,
  remove: () => undefined,
  messages: [],
};

function useSnackbar() {
  const [messages, setMessages] = useState<SnackbarMessage[]>([]);
  const add = useCallback(
    (message: SnackbarMessage) => {
      setMessages([...messages, message]);
    },
    [messages]
  );
  const remove = useCallback(
    (id: number) => {
      setMessages([...messages.filter((v) => v.id !== id)]);
    },
    [messages]
  );
  return {
    add,
    remove,
    messages,
  };
}

export const SnackbarContext = createContext<SnackbarStore>(initialSnackbarContext);

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const { add, remove, messages: snackbarMessages } = useSnackbar();
  return (
    <SnackbarContext.Provider value={{ add, remove, messages: snackbarMessages }}>
      {children}
      {snackbarMessages.map((snackbarMessage) => {
        const { id, message, notificationLevel, hideCloseButon } = snackbarMessage;
        return (
          <Snackbar
            key={id}
            id={id}
            message={message}
            notificationLevel={notificationLevel}
            hideCloseButon={hideCloseButon}
          />
        );
      })}
    </SnackbarContext.Provider>
  );
}
